import axios from 'axios';
import { Sentry } from '../../Utils/sentry';
import { checkCredentials } from '../../Contexts/AuthContext';
class AxiosWrapper {
  constructor(baseURL, username, password, setLoading) {
    this.baseURL = baseURL;
    this.username = username;
    this.password = password;
    this.setLoading = setLoading || (() => {});
    this.axiosInstance = this.createInstance();
  }

  createInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 180000,
    });

    instance.interceptors.request.use(
      (config) => {
        if (this.setLoading) this.setLoading(true);
        return config;
      },
      (error) => {
        if (this.setLoading) this.setLoading(false);
        return Promise.reject(error instanceof Error ? error : new Error(error));
      },
    );

    instance.interceptors.response.use(
      (response) => {
        if (this.setLoading) this.setLoading(false);
        if (
          response?.status === 401
          || response?.data?.status === 401
          || response?.data?.statusCode === 401
          || response?.data?.response?.status === 401
        ) {
          if (typeof window.handleSessionExpired === 'function') {
            window.handleSessionExpired();
          }
        }
        return response;
      },
      (error) => {
        if (this.setLoading) this.setLoading(false);
        if (
          error?.response?.status === 401
          || error?.status === 401
          || error?.response?.data?.status === 401
          || error?.response?.data?.statusCode === 401
        ) {
          if (typeof window.handleSessionExpired === 'function') {
            window.handleSessionExpired();
          }
        }
        if (error.response) {
          Sentry.captureException(new Error(`API Error: ${error.response.status} ${error.response.statusText}`));
        } else if (error.request) {
          Sentry.captureException(new Error('API Error: No response received'));
        } else {
          Sentry.captureException(error);
        }
        return Promise.reject(error instanceof Error ? error : new Error(error));
      },
    );

    return instance;
  }

  setAuthToken(token) {
    localStorage.setItem('authToken', token);
  }

  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  validateToken() {
    return new Promise((resolve, reject) => {
      const token = this.getAuthToken();
      if (!token) {
        resolve(false);
      } else {
        checkCredentials()
          .then((response) => {
            if (!response) {
              this.deauthenticate();
              resolve(false);
            } else {
              resolve(response);
            }
          })
          .catch((error) => {
            console.error(error);
            reject(error instanceof Error ? error : new Error(error));
          });
      }
    });
  }

  removeAuthToken() {
    localStorage.removeItem('authToken');
  }

  authenticate(token) {
    this.setAuthToken(token);
  }

  deauthenticate() {
    this.removeAuthToken();
  }

  get(url, params = {}) {
    return this.axiosInstance.get(url, { params });
  }

  post(url, data = {}) {
    return this.axiosInstance.post(url, data);
  }

  put(url, data = {}) {
    return this.axiosInstance.put(url, data);
  }

  delete(url, params = {}) {
    return this.axiosInstance.delete(url, { params });
  }
}

export default AxiosWrapper;
